/**
 * fl-marquee.js — keeps every .fl-marquee band gap-free at any viewport width.
 *
 * The CSS animates .fl-marquee__inner via translateX(-50%), so for a seamless
 * loop each HALF of the inner must be at least as wide as the band. The
 * markup ships with a fixed number of copies sized for narrow viewports; on
 * wide bands that under-fills and the loop visibly snaps. This script
 * measures the band and rebuilds the inner with enough copies per half to
 * always cover it, in two identical halves.
 */
(function () {
  'use strict';

  function debounce(fn, wait) {
    var timer;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  // Original markup duplicates one "copy" into two identical halves already
  // (a copy may be a single element or a run of several sibling elements).
  // Capture that first half's outerHTML once and cache it on the element so
  // repeated runs (resize) stay idempotent.
  function getCanonicalCopy(inner) {
    var cached = inner.getAttribute('data-fl-marquee-copy');
    if (cached !== null) return cached;

    var children = inner.children;
    var half = Math.floor(children.length / 2);
    if (half < 1) half = children.length; // odd/unexpected markup: use everything as one copy

    var html = '';
    for (var i = 0; i < half; i++) {
      html += children[i].outerHTML;
    }
    if (!html) html = inner.innerHTML;

    inner.setAttribute('data-fl-marquee-copy', html);
    return html;
  }

  function getOriginalDuration(inner) {
    var cached = inner.getAttribute('data-fl-marquee-duration');
    if (cached !== null) return parseFloat(cached) || 0;

    var computed = window.getComputedStyle(inner).animationDuration || '';
    var seconds = parseFloat(computed) || 0;
    inner.setAttribute('data-fl-marquee-duration', String(seconds));
    return seconds;
  }

  // Render one copy off-screen (inside a .fl-marquee ancestor so the
  // ".fl-marquee span" descendant styles actually apply) to get its true
  // rendered width.
  function measureCopyWidth(html) {
    var probe = document.createElement('div');
    probe.className = 'fl-marquee';
    probe.style.position = 'absolute';
    probe.style.left = '-9999px';
    probe.style.top = '0';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';

    var probeInner = document.createElement('div');
    probeInner.style.display = 'inline-block';
    probeInner.style.whiteSpace = 'nowrap';
    probeInner.innerHTML = html;

    probe.appendChild(probeInner);
    document.body.appendChild(probe);
    var width = probeInner.getBoundingClientRect().width || probeInner.scrollWidth || 0;
    document.body.removeChild(probe);
    return width;
  }

  function fillMarquee(marquee) {
    var inner = marquee.querySelector('.fl-marquee__inner');
    if (!inner) return;

    var copyHTML = getCanonicalCopy(inner);
    if (!copyHTML) return;

    var bandWidth = marquee.offsetWidth;
    var copyWidth = measureCopyWidth(copyHTML);

    var copiesPerHalf;
    if (copyWidth <= 0) {
      copiesPerHalf = 1; // guard: fall back to two copies total
    } else {
      copiesPerHalf = Math.max(1, Math.ceil(bandWidth / copyWidth) + 1);
    }

    var halfHTML = '';
    for (var i = 0; i < copiesPerHalf; i++) {
      halfHTML += copyHTML;
    }
    inner.innerHTML = halfHTML + halfHTML;

    // Accessibility: an existing band/parent label is the one real reading;
    // otherwise expose the first copy and hide every visual repeat.
    var hasAccessibleLabel =
      marquee.hasAttribute('aria-label') ||
      (marquee.parentElement && marquee.parentElement.hasAttribute('aria-label'));

    var children = inner.children;
    var totalCopies = copiesPerHalf * 2;
    var childrenPerCopy = totalCopies > 0 ? Math.max(1, Math.round(children.length / totalCopies)) : children.length;
    for (var j = 0; j < children.length; j++) {
      if (!hasAccessibleLabel && j < childrenPerCopy) {
        children[j].removeAttribute('aria-hidden');
      } else {
        children[j].setAttribute('aria-hidden', 'true');
      }
    }

    // Optional polish: keep scroll speed (px/s) constant across widths by
    // scaling the animation duration with the number of copies per half.
    var originalDuration = getOriginalDuration(inner);
    if (originalDuration > 0) {
      inner.style.animationDuration = (originalDuration * copiesPerHalf) + 's';
    }
  }

  function fillAllMarquees() {
    var marquees = document.querySelectorAll('.fl-marquee');
    for (var i = 0; i < marquees.length; i++) {
      fillMarquee(marquees[i]);
    }
  }

  var debouncedFill = debounce(fillAllMarquees, 150);

  function init() {
    fillAllMarquees();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fillAllMarquees);
    }
    window.addEventListener('resize', debouncedFill);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
